import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { useSupabase } from '../../../contexts/SupabaseContext';

interface OptionCardProps {
  option: any;
}

const OptionCard: React.FC<OptionCardProps> = ({ option }) => {
  const {
    updateDecisionOption,
    deleteDecisionOption,
    decisionOptionPoints,
    loadDecisionOptionPoints,
    createDecisionOptionPoint,
    updateDecisionOptionPoint,
    deleteDecisionOptionPoint,
  } = useSupabase();

  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(option.text);
  const [newPro, setNewPro] = useState('');
  const [newCon, setNewCon] = useState('');

  useEffect(() => {
    loadDecisionOptionPoints(option.id);
  }, [option.id, loadDecisionOptionPoints]);

  const points = decisionOptionPoints[option.id] || [];
  const pros = points.filter((p) => p.type === 'pro');
  const cons = points.filter((p) => p.type === 'con');

  return (
    <Card className="bg-muted/40">
      <CardContent className="p-3 space-y-1">
        <div className="flex items-center gap-2">
          {editing ? (
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={async () => {
                try {
                  await updateDecisionOption(option.id, { text });
                  setEditing(false);
                } catch (error: any) {
                  console.error(error.message || JSON.stringify(error));
                  alert(error.message || 'An error occurred');
                }
              }}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  try {
                    await updateDecisionOption(option.id, { text });
                    setEditing(false);
                  } catch (error: any) {
                    console.error(error.message || JSON.stringify(error));
                    alert(error.message || 'An error occurred');
                  }
                }
              }}
              className="font-medium text-sm"
              autoFocus
            />
          ) : (
            <span className="font-medium text-sm" onDoubleClick={() => setEditing(true)}>{option.text}</span>
          )}
          <Button variant="ghost" size="icon" onClick={() => setEditing(true)}><Edit2 size={14} /></Button>
          <Button variant="ghost" size="icon" onClick={() => deleteDecisionOption(option.id)}><Trash2 size={14} /></Button>
        </div>
        <div className="flex gap-4 mt-2">
          {/* Pros */}
          <div className="flex-1">
            <div className="font-semibold text-xs mb-1">Pros</div>
            <ul className="list-disc ml-4 text-xs">
              {pros.map((pro: any) => (
                <li key={pro.id} className="flex items-center justify-between">
                  <span>{pro.text}</span>
                  <Button variant="ghost" size="icon" onClick={() => deleteDecisionOptionPoint(pro.id)}><Trash2 size={12} /></Button>
                </li>
              ))}
            </ul>
            <div className="flex gap-1 mt-1">
              <Input
                value={newPro}
                onChange={(e) => setNewPro(e.target.value)}
                placeholder="Add pro..."
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newPro.trim()) {
                    try {
                      await createDecisionOptionPoint({ option_id: option.id, type: 'pro', text: newPro });
                      setNewPro('');
                    } catch (error: any) {
                      console.error(error.message || JSON.stringify(error));
                      alert(error.message || 'An error occurred');
                    }
                  }
                }}
                className="text-xs"
              />
              <Button size="sm" onClick={async () => {
                if (newPro.trim()) {
                  try {
                    await createDecisionOptionPoint({ option_id: option.id, type: 'pro', text: newPro });
                    setNewPro('');
                  } catch (error: any) {
                    console.error(error.message || JSON.stringify(error));
                    alert(error.message || 'An error occurred');
                  }
                }
              }}><Plus size={12} /></Button>
            </div>
          </div>
          {/* Cons */}
          <div className="flex-1">
            <div className="font-semibold text-xs mb-1">Cons</div>
            <ul className="list-disc ml-4 text-xs">
              {cons.map((con: any) => (
                <li key={con.id} className="flex items-center justify-between">
                  <span>{con.text}</span>
                  <Button variant="ghost" size="icon" onClick={() => deleteDecisionOptionPoint(con.id)}><Trash2 size={12} /></Button>
                </li>
              ))}
            </ul>
            <div className="flex gap-1 mt-1">
              <Input
                value={newCon}
                onChange={(e) => setNewCon(e.target.value)}
                placeholder="Add con..."
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newCon.trim()) {
                    try {
                      await createDecisionOptionPoint({ option_id: option.id, type: 'con', text: newCon });
                      setNewCon('');
                    } catch (error: any) {
                      console.error(error.message || JSON.stringify(error));
                      alert(error.message || 'An error occurred');
                    }
                  }
                }}
                className="text-xs"
              />
              <Button size="sm" onClick={async () => {
                if (newCon.trim()) {
                  try {
                    await createDecisionOptionPoint({ option_id: option.id, type: 'con', text: newCon });
                    setNewCon('');
                  } catch (error: any) {
                    console.error(error.message || JSON.stringify(error));
                    alert(error.message || 'An error occurred');
                  }
                }
              }}><Plus size={12} /></Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default OptionCard; 